/**
 * api.ts — `useEkipSchedule` + accept/complete mutations.
 *
 * Endpoints:
 *   GET  /ekip/schedule
 *   POST /ekip/confirms/:orderId/accept
 *   POST /ekip/confirms/:orderId/complete
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";
import { scheduleKeys } from "@/features/schedule/queryKeys";

export type EkipShift = components["schemas"]["EkipShift"];
export type EkipDayGroup = components["schemas"]["EkipDayGroup"];
export type EkipScheduleView = components["schemas"]["EkipScheduleView"];

export const ekipScheduleKeys = {
  view: () => ["ekip", "schedule"] as const,
};

export function useEkipSchedule() {
  return useQuery({
    queryKey: ekipScheduleKeys.view(),
    queryFn: async () => {
      const { data, error } = await api.GET("/ekip/schedule", {});
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được lịch ekip"));
      return data;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useAcceptShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderId: string) => {
      const { data, error } = await api.POST(
        "/ekip/confirms/{orderId}/accept",
        { params: { path: { orderId } } },
      );
      if (error || !data) throw new Error(serverErrorMessage(error, "Nhận ca thất bại"));
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ekipScheduleKeys.view() });
      void qc.invalidateQueries({ queryKey: ["tasks", "me"] });
      void qc.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

type WorkInfoInput = {
  orderId: string;
  notes?: string;
  links?: string[];
  /** Hai thẻ ê-kíp (Sửa cẩn thận / Đặc biệt); undefined = không đụng. */
  tags?: string[];
};

export function useSaveWorkInfo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orderId, notes, links, tags }: WorkInfoInput) => {
      const { data, error } = await api.PUT("/ekip/orders/{orderId}/work-info", {
        params: { path: { orderId } },
        body: {
          ...(notes === undefined ? {} : { notes }),
          ...(links === undefined ? {} : { rawPhotoLinks: links }),
          ...(tags === undefined ? {} : { tags }),
        },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Lưu thông tin ca thất bại"));
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ekipScheduleKeys.view() });
      void qc.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

/**
 * useSetRawLinks — save the raw-photo link list for a shift through the
 * assigned-shift endpoint, so photo staff do not need broad order.write.
 */
export function useSetRawLinks() {
  return useSaveWorkInfo();
}

export function useCompleteShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderId: string) => {
      const { data, error } = await api.POST(
        "/ekip/confirms/{orderId}/complete",
        { params: { path: { orderId } } },
      );
      if (error || !data) throw new Error(serverErrorMessage(error, "Hoàn tất thất bại"));
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ekipScheduleKeys.view() });
      void qc.invalidateQueries({ queryKey: ["orders"] });
      // Hoàn tất makeup ở cửa sớm (QĐ 0261) đổi trạng thái đơn trên lưới Lịch chụp.
      void qc.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

/**
 * Thợ trang điểm khai SỐ MẶT CỦA CHÍNH MÌNH (quyết định 0194 điều 5).
 *
 * Cửa RIÊNG, không dùng `useSaveWorkEntries` của màn sổ công hợp đồng: cửa kia
 * thay TRỌN BỘ sổ của buổi, nên một thợ bấm lưu ở đó sẽ xoá sạch lời khai của
 * đồng nghiệp. Cửa này chỉ ghi đúng một ô.
 *
 * Máy chủ trả về CẢ sổ công sau khi ghi, nên đặt thẳng vào khoá đệm
 * `["quote","work-entries",orderId]` — cùng khoá `useOrderWorkEntries` đọc, để
 * con số trên thẻ ca đúng ngay khi bấm xong, không chờ vòng tải lại.
 */
export function useSetMyMakeupFaces(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    // `split` (QĐ 0287): khai theo nữ / nam / bé — máy chủ ghi tổng ba ô.
    mutationFn: async (vars: { faces?: number; split?: MakeupFaceSplit; note?: string }) => {
      const { data, error } = await api.PUT("/orders/{id}/work-entries/my-makeup-faces", {
        params: { path: { id: orderId } },
        body: {
          ...(vars.split ? { split: vars.split } : { faces: vars.faces ?? 0 }),
          ...(vars.note ? { note: vars.note } : {}),
        },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không lưu được số mặt"));
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["quote", "work-entries", orderId], data);
      // Thẻ ca đọc `missing` của buổi để nhắc còn thiếu gì.
      void qc.invalidateQueries({ queryKey: ekipScheduleKeys.view() });
    },
  });
}

export type MakeupFaceSplit = components["schemas"]["MakeupFaceSplit"];

/**
 * Người phân ê-kíp chia việc nữ / nam / bé cho TỪNG thợ trang điểm của buổi
 * (quyết định 0287). Thay trọn bộ sổ số mặt của buổi — màn chia việc hiện đủ ô
 * của mọi thợ. Lễ tân / kinh doanh chia thì sổ về chờ duyệt; trưởng cửa hàng
 * trở lên chia thì duyệt sẵn — máy chủ quyết, màn chỉ hiện trạng thái trả về.
 */
export function useAssignMakeupSplit(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entries: { staffId: string; split: MakeupFaceSplit }[]) => {
      const { data, error } = await api.PUT("/orders/{id}/work-entries/makeup-split", {
        params: { path: { id: orderId } },
        body: {
          entries: entries.map((e) => ({ staffId: e.staffId, faces: e.split.female + e.split.male + e.split.child, split: e.split })),
        },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không lưu được phần chia việc"));
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["quote", "work-entries", orderId], data);
      void qc.invalidateQueries({ queryKey: ekipScheduleKeys.view() });
    },
  });
}
